import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { Camera, CheckCircle2, ChevronLeft, ChevronRight, ImagePlus, Loader2, Send, Trash2, TriangleAlert } from 'lucide-react';
import type { CreateSupportTicketResponse, SupportKind } from '@lira/shared';
import { Modal } from '@/components/ui';
import { apiFormPost, apiPost } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { useAuthStore } from '@/stores/auth';
import { toast } from '@/stores/toast';

const MIN_DETAILS = 40;
const MAX_SCREENSHOT_BYTES = 2 * 1024 * 1024;
const PAGE_LABELS: Record<string, string> = {
  '/': 'Visão geral',
  '/transacoes': 'Transações',
  '/cartoes': 'Cartões',
  '/contas': 'Contas',
  '/categorias': 'Categorias',
  '/tags': 'Tags',
  '/relatorios': 'Relatórios',
  '/orcamentos': 'Orçamentos',
  '/mais': 'Mais',
  '/config': 'Configurações',
};

const KINDS: Array<{ value: SupportKind; label: string; hint: string }> = [
  { value: 'bug', label: 'Algo não funcionou', hint: 'Erro, comportamento estranho ou dado incorreto.' },
  { value: 'suggestion', label: 'Tenho uma sugestão', hint: 'Uma melhoria que deixaria o Lira mais útil.' },
  { value: 'question', label: 'Tenho uma dúvida', hint: 'Algo que você não encontrou ou não entendeu.' },
];

function pageLabel(pathname: string): string {
  return PAGE_LABELS[pathname] ?? (document.title.replace(/\s*[—|-]\s*Lira.*$/i, '').trim() || 'Tela atual');
}

function DetailHint({ value }: { value: string }) {
  const remaining = Math.max(0, MIN_DETAILS - value.trim().length);
  return (
    <p className={remaining ? 'mt-1 text-xs text-muted' : 'mt-1 text-xs text-income'}>
      {remaining ? `Faltam ${remaining} caracteres para continuar.` : 'Detalhe suficiente para continuar.'}
    </p>
  );
}

/** Banner individual; permanece legível por cinco segundos antes de liberar o fechamento. */
export function SupportIntroBanner() {
  const user = useAuthStore((s) => s.user);
  const [seconds, setSeconds] = useState(5);
  const dismiss = async () => {
    const result = await apiPost<{ supportIntroDismissedAt: string }>('/support/intro/dismiss');
    const state = useAuthStore.getState();
    if (state.user && state.accessToken) {
      state.setSession(state.accessToken, { ...state.user, supportIntroDismissedAt: result.supportIntroDismissedAt });
    }
  };

  useEffect(() => {
    if (user?.supportIntroDismissedAt || seconds === 0) return;
    const id = window.setTimeout(() => setSeconds((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(id);
  }, [seconds, user?.supportIntroDismissedAt]);

  if (!user || user.supportIntroDismissedAt) return null;
  return (
    <aside
      className="mx-auto mb-4 flex max-w-[72.5rem] items-center justify-between gap-2 rounded-[var(--radius-panel)] border border-primary/20 bg-primary-weak px-3 py-2 text-sm text-ink shadow-card sm:items-start sm:gap-4 sm:px-4 sm:py-3"
      aria-label="Conheça o canal de suporte"
    >
      <div className="min-w-0">
        <p className="truncate text-[13px] font-semibold text-primary sm:text-sm">Encontrou algo fora do esperado?</p>
        <p className="mt-0.5 hidden text-muted sm:block">Use “Reportar problema” para enviar contexto direto ao time. Captura de tela é opcional.</p>
      </div>
      <button
        type="button"
        onClick={() => void dismiss().catch((error: unknown) => toast(errorMessage(error), 'error'))}
        disabled={seconds > 0}
        className="min-h-11 shrink-0 rounded-full px-3 text-xs font-semibold text-primary transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-55"
      >
        {seconds > 0 ? `Ler (${seconds}s)` : 'Fechar'}
      </button>
    </aside>
  );
}

export function SupportReporter({ open, onClose }: { open: boolean; onClose: () => void }) {
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState(1);
  const [kind, setKind] = useState<SupportKind>('bug');
  const [happenedDetails, setHappenedDetails] = useState('');
  const [reproductionDetails, setReproductionDetails] = useState('');
  const [consent, setConsent] = useState(false);
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<CreateSupportTicketResponse | null>(null);
  const previewUrl = useMemo(() => (screenshot ? URL.createObjectURL(screenshot) : null), [screenshot]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);
  useEffect(() => {
    if (!open) return;
    setSubmitted(null);
  }, [open]);

  const canAdvance =
    (step === 1 && happenedDetails.trim().length >= MIN_DETAILS) ||
    (step === 2 && reproductionDetails.trim().length >= MIN_DETAILS) ||
    step === 3;
  const assignScreenshot = (file: File) => {
    if (file.type !== 'image/png') {
      setCaptureError('Envie somente uma imagem PNG.');
      return;
    }
    if (file.size > MAX_SCREENSHOT_BYTES) {
      setCaptureError('A imagem precisa ter no máximo 2 MiB.');
      return;
    }
    setCaptureError(null);
    setScreenshot(file);
  };
  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) assignScreenshot(file);
    event.target.value = '';
  };
  const capture = async () => {
    setCaptureError(null);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const target = document.querySelector('main') ?? document.body;
      const canvas = await html2canvas(target as HTMLElement, { backgroundColor: '#ffffff', scale: 1, useCORS: false });
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('Não foi possível gerar a imagem.');
      assignScreenshot(new File([blob], `reporte-${Date.now()}.png`, { type: 'image/png' }));
    } catch {
      setCaptureError('Não foi possível capturar esta tela. Você pode anexar um PNG manualmente ou enviar sem imagem.');
    }
  };
  const resetAndClose = () => {
    setStep(1);
    setKind('bug');
    setHappenedDetails('');
    setReproductionDetails('');
    setConsent(false);
    setScreenshot(null);
    setCaptureError(null);
    setSubmitted(null);
    onClose();
  };
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting || !canAdvance) return;
    setSubmitting(true);
    try {
      const body = new FormData();
      body.set('kind', kind);
      body.set('pagePath', location.pathname);
      body.set('pageLabel', pageLabel(location.pathname));
      body.set('happenedDetails', happenedDetails.trim());
      body.set('reproductionDetails', reproductionDetails.trim());
      if (screenshot) body.set('screenshot', screenshot);
      const result = await apiFormPost<CreateSupportTicketResponse>('/support/tickets', body);
      setSubmitted(result);
      toast('Reporte enviado. Obrigado por ajudar a melhorar o Lira.', 'success');
    } catch (error) {
      toast(errorMessage(error), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={submitting ? () => undefined : resetAndClose} title="Reportar problema" maxWidth={640}>
      {submitted ? (
        <div className="py-5 text-center">
          <CheckCircle2 className="mx-auto size-12 text-income" />
          <h3 className="mt-3 text-lg font-semibold text-ink">Recebemos seu reporte</h3>
          <p className="mt-1 text-sm text-muted">Código: <span className="font-mono text-ink">{submitted.id}</span></p>
          <button type="button" onClick={resetAndClose} className="mt-6 h-10 rounded-full bg-primary px-5 text-sm font-semibold uppercase tracking-wide text-white">Concluir</button>
        </div>
      ) : (
        <form onSubmit={submit}>
          <ol className="mb-5 grid grid-cols-3 gap-2 text-center text-xs font-semibold">
            {['O que ocorreu', 'Como repetir', 'Contexto'].map((label, index) => (
              <li key={label} className={step >= index + 1 ? 'rounded-full bg-primary-weak py-2 text-primary' : 'rounded-full bg-canvas py-2 text-muted'}>{index + 1}. {label}</li>
            ))}
          </ol>

          {step === 1 && <div className="space-y-4">
            <fieldset><legend className="text-sm font-semibold text-ink">Qual tipo de relato descreve melhor?</legend><div className="mt-2 grid gap-2">
              {KINDS.map((option) => <label key={option.value} className={`cursor-pointer rounded-lg border p-3 transition-colors ${kind === option.value ? 'border-primary bg-primary-weak' : 'border-field hover:border-muted'}`}><input className="sr-only" type="radio" checked={kind === option.value} onChange={() => setKind(option.value)} /><span className="block text-sm font-semibold text-ink">{option.label}</span><span className="mt-0.5 block text-xs text-muted">{option.hint}</span></label>)}
            </div></fieldset>
            <div><label htmlFor="support-happened" className="text-sm font-semibold text-ink">O que aconteceu?</label><textarea id="support-happened" required minLength={MIN_DETAILS} value={happenedDetails} onChange={(e) => setHappenedDetails(e.target.value)} rows={5} placeholder="Conte o que você esperava e o que aconteceu, com exemplos concretos." className="mt-2 w-full resize-y rounded-lg border border-field bg-surface p-3 text-sm text-ink outline-none focus:border-primary" /><DetailHint value={happenedDetails} /></div>
          </div>}

          {step === 2 && <div><label htmlFor="support-reproduction" className="text-sm font-semibold text-ink">Como podemos repetir?</label><p className="mt-1 text-xs text-muted">Descreva os passos, valores e resultado esperado.</p><textarea id="support-reproduction" required minLength={MIN_DETAILS} value={reproductionDetails} onChange={(e) => setReproductionDetails(e.target.value)} rows={7} placeholder="Ex.: entre em..., selecione..., salve... então acontece..." className="mt-2 w-full resize-y rounded-lg border border-field bg-surface p-3 text-sm text-ink outline-none focus:border-primary" /><DetailHint value={reproductionDetails} /></div>}

          {step === 3 && <div className="space-y-4"><div className="rounded-lg border border-border bg-canvas p-4"><div className="flex items-start gap-2"><TriangleAlert className="mt-0.5 size-4 shrink-0 text-primary" /><p className="text-sm text-muted">A captura é opcional e pode conter informações pessoais. Revise antes de enviar; ela ficará disponível apenas para administradores.</p></div><label className="mt-3 flex cursor-pointer items-start gap-2 text-sm text-ink"><input type="checkbox" checked={consent} onChange={(e) => { setConsent(e.target.checked); if (!e.target.checked) setScreenshot(null); }} className="mt-0.5" />Autorizo anexar uma captura de tela a este reporte.</label></div>
            {consent && <div className="rounded-lg border border-dashed border-field p-4"><div className="flex flex-wrap gap-2"><button type="button" onClick={() => void capture()} className="flex h-10 items-center gap-2 rounded-full bg-primary px-4 text-sm font-semibold text-white hover:bg-primary-hover"><Camera className="size-4" />Capturar tela atual</button><button type="button" onClick={() => inputRef.current?.click()} className="flex h-10 items-center gap-2 rounded-full border border-field px-4 text-sm font-semibold text-ink hover:bg-canvas"><ImagePlus className="size-4" />Anexar PNG</button><input ref={inputRef} type="file" accept="image/png" className="sr-only" onChange={onFileChange} /></div>{captureError && <p className="mt-3 text-sm text-expense">{captureError}</p>}{screenshot && previewUrl && <div className="mt-4"><img src={previewUrl} alt="Prévia da captura que será enviada" className="max-h-52 rounded-lg border border-border object-contain" /><div className="mt-2 flex items-center justify-between gap-3 text-xs text-muted"><span className="truncate">{screenshot.name} · {(screenshot.size / 1024).toFixed(0)} KB</span><button type="button" onClick={() => setScreenshot(null)} className="flex items-center gap-1 font-semibold text-expense"><Trash2 className="size-3.5" />Remover</button></div></div>}</div>}</div>}

          <div className="mt-6 flex justify-between gap-3"><button type="button" onClick={() => step === 1 ? resetAndClose() : setStep((value) => value - 1)} disabled={submitting} className="flex h-10 items-center gap-1 rounded-full px-4 text-sm font-semibold text-muted hover:bg-canvas hover:text-ink"><ChevronLeft className="size-4" />{step === 1 ? 'Cancelar' : 'Voltar'}</button>{step < 3 ? <button type="button" onClick={() => canAdvance && setStep((value) => value + 1)} disabled={!canAdvance} className="flex h-10 items-center gap-1 rounded-full bg-primary px-5 text-sm font-semibold text-white disabled:opacity-50">Continuar<ChevronRight className="size-4" /></button> : <button type="submit" disabled={submitting} className="flex h-10 items-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-white disabled:opacity-60">{submitting ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}Enviar reporte</button>}</div>
        </form>
      )}
    </Modal>
  );
}
